import { inject } from "@angular/core";
import { Router, type CanActivateFn } from "@angular/router";
import { AuthService } from "@app/common/services/auth.service";
import { PortfolioStateService } from "@app/parse-analyze-track/services/portfolio-state.service";
import { PublicPortfolioService } from "@app/parse-analyze-track/services/public-portfolio.service";
import { PortfolioAccessService } from "@app/parse-analyze-track/services/portfolio-access.service";
import { catchError, from, map, of, switchMap, take } from "rxjs";

export const portfolioAccessGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const portfolioState = inject(PortfolioStateService);
  const publicPortfolioService = inject(PublicPortfolioService);
  const portfolioAccessService = inject(PortfolioAccessService);

  const rawPortfolioId = route.queryParamMap.get("pid");
  if (rawPortfolioId === null) {
    const tab = route.paramMap.get("tab");
    return authService.currentUser$.pipe(
      take(1),
      map((user) => {
        if (!user) {
          return router.createUrlTree(["/parse-analyze-track/portfolios"]);
        }
        return tab === "summary"
          ? true
          : router.createUrlTree([
              "/parse-analyze-track/portfolio/summary",
            ]);
      })
    );
  }

  const portfolioId = rawPortfolioId ? Number(rawPortfolioId) : Number.NaN;
  if (!Number.isFinite(portfolioId) || portfolioId <= 0) {
    return router.createUrlTree(["/parse-analyze-track/portfolios"]);
  }

  return authService.currentUser$.pipe(
    take(1),
    switchMap((user) => {
      const existing = portfolioState.getById(portfolioId);
      if (existing && user && existing.userId === user.uid) {
        return of(true);
      }

      if (!user) {
        if (existing?.isPublic) return of(true);
        return publicPortfolioService.getPublicPortfolioById(portfolioId).pipe(
          map((summary) => {
            if (!summary) {
              return router.createUrlTree(["/parse-analyze-track/portfolios"]);
            }
            portfolioState.upsertPortfolio(
              publicPortfolioService.toPortfolioEntity(summary)
            );
            return true;
          }),
          catchError(() =>
            of(router.createUrlTree(["/parse-analyze-track/portfolios"]))
          )
        );
      }

      const loadForAuthenticatedViewer = (
        canWrite: boolean,
        elevatedAccess: boolean,
      ) =>
        publicPortfolioService
          .getPortfolioForViewer(portfolioId, user.uid, elevatedAccess)
          .pipe(
            map((summary) =>
              summary
                ? {
                    ...summary,
                    viewerCanWrite: canWrite,
                    viewerCanWriteForUserId: user.uid,
                  }
                : null
            )
          );

      return from(portfolioAccessService.resolve(portfolioId, user)).pipe(
        switchMap((access) => {
          if (!access.canView) return of(null);
          return loadForAuthenticatedViewer(access.canWrite, access.canWrite);
        }),
        // Keep ordinary public links available if the capability service is
        // temporarily unavailable. Private administrator access remains
        // fail-closed because the public loader will return null.
        catchError(() => loadForAuthenticatedViewer(false, false)),
        map((summary) => {
          if (!summary) {
            return router.createUrlTree(["/parse-analyze-track/portfolios"]);
          }
          portfolioState.upsertPortfolio(
            publicPortfolioService.toPortfolioEntity(summary)
          );
          return true;
        }),
        catchError(() =>
          of(router.createUrlTree(["/parse-analyze-track/portfolios"]))
        )
      );
    })
  );
};
